import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AlertTriangle, CheckCircle, Clock, CalendarDays, DollarSign } from 'lucide-react'
import type { EstadisticasVencimientos as EstadisticasType } from '@/types/vencimientos'

interface EstadisticasVencimientosProps {
  estadisticas: EstadisticasType
  isLoading?: boolean
}

interface CardConfig {
  title: string
  value: number
  icon: React.ElementType
  color: string
  bgColor: string
  description: string
  tooltip: string
  highlightIfPositive?: boolean
}

export function EstadisticasVencimientos({
  estadisticas,
  isLoading = false,
}: EstadisticasVencimientosProps) {
  const cards: CardConfig[] = [
    {
      title: 'Vencidos',
      value: estadisticas.vencidos,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      description: 'Requieren atención',
      tooltip: 'Vencimientos pendientes cuya fecha de vencimiento ya pasó. No presentados ni pagados.',
      highlightIfPositive: true,
    },
    {
      title: 'Vencen esta semana',
      value: estadisticas.vencenEstaSemana,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      description: 'Próximos 7 días',
      tooltip: 'Vencimientos pendientes con fecha entre hoy y los próximos 7 días.',
    },
    {
      title: 'Vencen este mes',
      value: estadisticas.vencenEsteMes,
      icon: CalendarDays,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Hasta fin de mes',
      tooltip: 'Vencimientos pendientes con fecha entre hoy y el fin del mes calendario actual.',
    },
    {
      title: 'Presentados este mes',
      value: estadisticas.presentadosEsteMes,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      description: 'Productividad del mes',
      tooltip: 'Vencimientos marcados como presentados durante el mes calendario en curso.',
    },
    {
      title: 'Pagados este mes',
      value: estadisticas.pagadosEsteMes,
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Cobros del mes',
      tooltip: 'Vencimientos con pago registrado durante el mes calendario en curso.',
    },
  ]

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              <div className="h-8 w-8 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-7 w-16 bg-muted animate-pulse rounded mb-1" />
              <div className="h-3 w-20 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {cards.map((card) => (
          <Tooltip key={card.title}>
            <TooltipTrigger asChild>
              <Card className="cursor-default">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {card.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${card.bgColor}`}>
                    <card.icon className={`h-4 w-4 ${card.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${card.highlightIfPositive && card.value > 0 ? 'text-red-600' : ''}`}>
                    {card.value}
                  </div>
                  <p className="text-xs text-muted-foreground">{card.description}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-56 text-center">
              <p>{card.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}
